// Money in: what customers owe, payments received, credits and write-offs.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HandCoins, Receipt, Scissors, XCircle } from "lucide-react";

import { api, formatApiError } from "../../lib/api";
import { CAPABILITIES, CHARGE_CATEGORIES, PAYMENT_METHODS, can, idemHeaders, todayISO } from "../../lib/books";
import { Button, Empty, Field, Loading, Modal, Money, Pill, Section, Table, inputClass } from "./shared";

export default function MoneyTab({ status, accounts, onChanged }) {
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);      // {type, charge}
  const canPay = can(status, CAPABILITIES.recordPayment);
  const canPost = can(status, CAPABILITIES.post);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/reports/ar-aging");
      setAging(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const done = () => { setAction(null); load(); onChanged?.(); };

  if (loading) return <Loading label="Reading receivables..." />;

  return (
    <div className="space-y-4" data-testid="books-money">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {["current", "1_30", "31_60", "61_90", "over_90"].map((bucket) => (
          <div key={bucket} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
              {bucket === "current" ? "Not due" : bucket === "over_90" ? "90+ days" : `${bucket.replace("_", "-")} days`}
            </div>
            <div className="mt-1 font-semibold tabular-nums">
              <Money minor={(aging?.totals || {})[bucket]} />
            </div>
          </div>
        ))}
      </div>

      <Section title="Customers who owe you"
               description={aging?.reconciled === false
                 ? "The ledger and these documents disagree - run the integrity check."
                 : "Every open charge, oldest first."}
               actions={canPost && (
                 <Button variant="secondary" onClick={() => setAction({ type: "charge" })}
                         data-testid="books-new-charge">
                   <Receipt className="w-4 h-4" /> New customer charge
                 </Button>)}
               testId="books-receivables">
        <Table head={["Document", "Booking", "Date", { key: "open", label: "Open", align: "right" }, ""]}
               empty={(aging?.rows || []).length === 0 && <Empty>Nothing is outstanding.</Empty>}>
          {(aging?.rows || []).map((row) => (
            <tr key={row.document_id} data-testid={`books-ar-${row.number}`}>
              <td className="py-2 pr-3">
                <div className="font-medium text-slate-900">{row.number}</div>
                <div className="text-xs text-slate-500">{row.customer_name || "Customer"}</div>
              </td>
              <td className="py-2 pr-3 text-slate-600">{row.booking_reference || "-"}</td>
              <td className="py-2 pr-3 text-slate-600">
                {row.date} <Pill status={row.bucket === "current" ? "posted" : "warning"}>{row.age_days}d</Pill>
              </td>
              <td className="py-2 pr-3 text-right"><Money minor={row.open_minor} /></td>
              <td className="py-2 pr-3">
                <div className="flex flex-wrap justify-end gap-1">
                  {canPay && (
                    <Button variant="secondary" onClick={() => setAction({ type: "payment", charge: row })}
                            data-testid={`books-pay-${row.number}`}>
                      <HandCoins className="w-4 h-4" /> Payment
                    </Button>
                  )}
                  {canPost && (
                    <>
                      <Button variant="secondary" onClick={() => setAction({ type: "credit", charge: row })}
                              data-testid={`books-credit-${row.number}`}>
                        <Scissors className="w-4 h-4" /> Credit
                      </Button>
                      <Button variant="secondary" onClick={() => setAction({ type: "write_off", charge: row })}
                              data-testid={`books-writeoff-${row.number}`}>
                        <XCircle className="w-4 h-4" /> Write off
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      {action?.type === "payment" && (
        <PaymentModal charge={action.charge} accounts={accounts} onClose={() => setAction(null)} onDone={done} />
      )}
      {action?.type === "credit" && (
        <CreditNoteModal charge={action.charge} accounts={accounts} onClose={() => setAction(null)} onDone={done} />
      )}
      {action?.type === "write_off" && (
        <WriteOffModal charge={action.charge} onClose={() => setAction(null)} onDone={done} />
      )}
      {action?.type === "charge" && (
        <CustomerChargeModal onClose={() => setAction(null)} onDone={done} />
      )}
    </div>
  );
}

function PaymentAccountField({ value, onChange, accounts, method, onMethod }) {
  return (
    <>
      <Field label="How was it paid?">
        <select className={inputClass} value={method} onChange={(e) => onMethod(e.target.value)}
                data-testid="books-payment-method">
          {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </Field>
      <Field label="Into which account?" hint="Leave as mapped to use your payment method settings.">
        <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}
                data-testid="books-payment-account">
          <option value="">Use my settings</option>
          {(accounts || []).filter((a) => a.is_payment_account && a.active)
            .map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
        </select>
      </Field>
    </>
  );
}

function PaymentModal({ charge, accounts, onClose, onDone }) {
  const [form, setForm] = useState({ amount: (charge.open_minor / 100).toFixed(2), method: "cash",
                                     payment_account_id: "", reference: "", date: todayISO() });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/payments", {
        amount: form.amount, method: form.method, date: form.date,
        payment_account_id: form.payment_account_id || null, reference: form.reference || null,
        allocations: [{ document_id: charge.document_id, amount: form.amount }],
      }, { headers: idemHeaders() });
      toast.success(`Recorded ${data.document.number}.`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Payment for ${charge.number}`} onClose={onClose} testId="books-payment-modal"
           description={`Open balance ${(charge.open_minor / 100).toLocaleString("en-PH", { style: "currency", currency: "PHP" })}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Amount (₱)">
          <input className={inputClass} inputMode="decimal" value={form.amount} data-testid="books-payment-amount"
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        <Field label="Date received">
          <input type="date" className={inputClass} value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        <PaymentAccountField value={form.payment_account_id} accounts={accounts} method={form.method}
                             onChange={(v) => setForm({ ...form, payment_account_id: v })}
                             onMethod={(v) => setForm({ ...form, method: v })} />
        <Field label="Reference (optional)" className="sm:col-span-2">
          <input className={inputClass} value={form.reference} maxLength={120}
                 onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        </Field>
      </div>
      <Button onClick={submit} busy={busy} data-testid="books-payment-save">Record payment</Button>
    </Modal>
  );
}

function CreditNoteModal({ charge, accounts, onClose, onDone }) {
  const [form, setForm] = useState({ amount: "", reason: "", excess_handling: "", method: "cash",
                                     payment_account_id: "", date: todayISO() });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/finance/credit-notes", {
        charge_id: charge.document_id, amount: form.amount, reason: form.reason, date: form.date,
        excess_handling: form.excess_handling || null, method: form.method,
        payment_account_id: form.payment_account_id || null,
      }, { headers: idemHeaders() });
      toast.success("Credit note posted.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Credit note on ${charge.number}`} onClose={onClose} testId="books-credit-modal"
           description="Reduces what the customer owes. If part of it was already paid, say where that money goes.">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Amount (₱)">
          <input className={inputClass} inputMode="decimal" value={form.amount} data-testid="books-credit-amount"
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        <Field label="Date">
          <input type="date" className={inputClass} value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        <Field label="Reason" className="sm:col-span-2">
          <input className={inputClass} value={form.reason} data-testid="books-credit-reason"
                 onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
        <Field label="If it was already paid">
          <select className={inputClass} value={form.excess_handling} data-testid="books-credit-excess"
                  onChange={(e) => setForm({ ...form, excess_handling: e.target.value })}>
            <option value="">Only reduce what is still owed</option>
            <option value="refund">Refund the difference</option>
            <option value="customer_credit">Hold it as customer credit</option>
          </select>
        </Field>
        {form.excess_handling === "refund" && (
          <PaymentAccountField value={form.payment_account_id} accounts={accounts} method={form.method}
                               onChange={(v) => setForm({ ...form, payment_account_id: v })}
                               onMethod={(v) => setForm({ ...form, method: v })} />
        )}
      </div>
      <Button onClick={submit} busy={busy} data-testid="books-credit-save">Post credit note</Button>
    </Modal>
  );
}

function WriteOffModal({ charge, onClose, onDone }) {
  const [form, setForm] = useState({ amount: (charge.open_minor / 100).toFixed(2), reason: "", date: todayISO() });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/finance/write-offs", { charge_id: charge.document_id, ...form },
                     { headers: idemHeaders() });
      toast.success("Written off as a bad debt.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Write off ${charge.number}`} onClose={onClose} testId="books-writeoff-modal"
           description="Use this only when the money will not be collected. It becomes a bad-debt expense.">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Amount (₱)">
          <input className={inputClass} inputMode="decimal" value={form.amount} data-testid="books-writeoff-amount"
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        <Field label="Date">
          <input type="date" className={inputClass} value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        <Field label="Reason" className="sm:col-span-2">
          <input className={inputClass} value={form.reason} data-testid="books-writeoff-reason"
                 onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </div>
      <Button variant="danger" onClick={submit} busy={busy} data-testid="books-writeoff-save">Write off</Button>
    </Modal>
  );
}

function CustomerChargeModal({ onClose, onDone }) {
  const [bookings, setBookings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState({ booking_id: "", category: "damage", amount: "", description: "",
                                     date: todayISO(), evidence_id: "" });
  const [busy, setBusy] = useState(false);
  const meta = CHARGE_CATEGORIES.find((c) => c.value === form.category);

  useEffect(() => {
    api.get("/bookings").then(({ data }) => setBookings((data || []).slice(0, 200))).catch(() => {});
    api.get("/administration/documents").then(({ data }) => setDocuments(data.items || [])).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/finance/customer-charges", {
        booking_id: form.booking_id || null, category: form.category, amount: form.amount,
        description: form.description, date: form.date,
        evidence_refs: form.evidence_id ? [{ type: "business_document", id: form.evidence_id }] : [],
      }, { headers: idemHeaders() });
      toast.success("Charge posted.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New customer charge" onClose={onClose} testId="books-charge-modal"
           description="For damage, fuel, late return and similar charges assessed after a rental.">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Booking">
          <select className={inputClass} value={form.booking_id} data-testid="books-charge-booking"
                  onChange={(e) => setForm({ ...form, booking_id: e.target.value })}>
            <option value="">Select a booking</option>
            {bookings.map((b) => (
              <option key={b.id} value={b.id}>{b.booking_reference} · {b.customer_name}</option>
            ))}
          </select>
        </Field>
        <Field label="Type of charge">
          <select className={inputClass} value={form.category} data-testid="books-charge-category"
                  onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CHARGE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Amount (₱)">
          <input className={inputClass} inputMode="decimal" value={form.amount} data-testid="books-charge-amount"
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        <Field label="Date">
          <input type="date" className={inputClass} value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        <Field label="What is it for?" className="sm:col-span-2">
          <input className={inputClass} value={form.description} data-testid="books-charge-description"
                 onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        {meta?.evidence && (
          <Field label="Evidence (required for this type)" className="sm:col-span-2"
                 hint="Pick a business document (photo, quote or report) you have already uploaded.">
            <select className={inputClass} value={form.evidence_id} data-testid="books-charge-evidence"
                    onChange={(e) => setForm({ ...form, evidence_id: e.target.value })}>
              <option value="">Select a document</option>
              {documents.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </Field>
        )}
      </div>
      <Button onClick={submit} busy={busy} data-testid="books-charge-save">Post charge</Button>
    </Modal>
  );
}
